import React, { useState, useMemo } from 'react';
import { Plus, Users, FileSpreadsheet, FileText, RefreshCw } from 'lucide-react';
import FeeSummaryCards from './FeeSummaryCards';
import FeeFiltersBar from './FeeFiltersBar';
import FeeAdvancedFiltersDrawer from './FeeAdvancedFiltersDrawer';
import FeeTable from './FeeTable';
import FeePagination from './FeePagination';
import FeeStudentDrawer from './FeeStudentDrawer';
import FeeHistoryDrawer from './FeeHistoryDrawer';
import FeeClearanceCertificateModal from './FeeClearanceCertificateModal';

const FeeManagementWorkspace = ({
  students = [],
  fees = [],
  feeStructures = [],
  loading = false,
  orgData,
  userRole,
  feeService,
  loadFees,
  handleOpenEditFeeModal,
  setSelectedStudentForCollection,
  setCollectionFeeRecord,
  setCollectionPayments,
  setFeesSubTab,
  setSelectedStructure,
  setStructureFormData,
  setShowCreateStructureModal,
  setAssignPreview,
  setAssignFormData,
  setShowAssignStructureModal,
  handleExportFeeExcel,
  handleExportFeePDF,
  setSelectedStudentForHistory,
  setShowHistoryModal,
  setLoadingPaymentHistory,
  setHistoryPayments,
  setAlertTargetStudents,
  setAlertCustomTitle,
  setAlertCustomMessage,
  setShowFeeAlertModal,
  setDraftToast,
  showNotification,
  searchTerm: propSearchTerm,
  setSearchTerm: propSetSearchTerm,
  filters: propFilters,
  setFilters: propSetFilters,
  customMinDueAmount: propCustomMinDueAmount,
  setCustomMinDueAmount: propSetCustomMinDueAmount,
  customMaxDueAmount: propCustomMaxDueAmount,
  setCustomMaxDueAmount: propSetCustomMaxDueAmount,
  showAdvancedFilters: propShowAdvancedFilters,
  setShowAdvancedFilters: propSetShowAdvancedFilters,
  currentPage: propCurrentPage,
  setCurrentPage: propSetCurrentPage,
  pageSize: propPageSize,
  setPageSize: propSetPageSize
}) => {
  // 1. Filter and Search State (supports controlled from parent or internal fallback)
  const [internalSearchTerm, setInternalSearchTerm] = useState('');
  const [internalFilters, setInternalFilters] = useState({
    class: '',
    section: '',
    paymentStatus: '',
    dueAmountFilter: '',
    feeStructureId: ''
  });
  const [internalCustomMinDueAmount, setInternalCustomMinDueAmount] = useState('');
  const [internalCustomMaxDueAmount, setInternalCustomMaxDueAmount] = useState('');
  const [internalShowAdvancedFilters, setInternalShowAdvancedFilters] = useState(false);

  // 2. Pagination State
  const [internalCurrentPage, setInternalCurrentPage] = useState(1);
  const [internalPageSize, setInternalPageSize] = useState(20);

  const searchTerm = propSearchTerm !== undefined ? propSearchTerm : internalSearchTerm;
  const setSearchTerm = propSetSearchTerm || setInternalSearchTerm;

  const filters = propFilters !== undefined ? propFilters : internalFilters;
  const setFilters = propSetFilters || setInternalFilters;

  const customMinDueAmount = propCustomMinDueAmount !== undefined ? propCustomMinDueAmount : internalCustomMinDueAmount;
  const setCustomMinDueAmount = propSetCustomMinDueAmount || setInternalCustomMinDueAmount;

  const customMaxDueAmount = propCustomMaxDueAmount !== undefined ? propCustomMaxDueAmount : internalCustomMaxDueAmount;
  const setCustomMaxDueAmount = propSetCustomMaxDueAmount || setInternalCustomMaxDueAmount;

  const showAdvancedFilters = propShowAdvancedFilters !== undefined ? propShowAdvancedFilters : internalShowAdvancedFilters;
  const setShowAdvancedFilters = propSetShowAdvancedFilters || setInternalShowAdvancedFilters;

  const currentPage = propCurrentPage !== undefined ? propCurrentPage : internalCurrentPage;
  const setCurrentPage = propSetCurrentPage || setInternalCurrentPage;

  const pageSize = propPageSize !== undefined ? propPageSize : internalPageSize;
  const setPageSize = propSetPageSize || setInternalPageSize;

  // 3. Selection State
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);

  // 4. Drawers State
  const [studentForDrawer, setStudentForDrawer] = useState(null);
  const [studentForHistoryDrawer, setStudentForHistoryDrawer] = useState(null);

  // 5. Fee Clearance Certificate Modal State
  const [clearanceModalData, setClearanceModalData] = useState({ isOpen: false, student: null, fee: null });

  const handleOpenClearanceModal = (student, fee) => {
    setClearanceModalData({
      isOpen: true,
      student,
      fee
    });
  };

  // Available classes list from students
  const availableClasses = useMemo(() => {
    return Array.from(new Set(students.map((s) => s.className).filter(Boolean))).sort();
  }, [students]);

  // Count active advanced filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.class) count++;
    if (filters.section) count++;
    if (filters.paymentStatus) count++;
    if (filters.dueAmountFilter) count++;
    if (filters.feeStructureId) count++;
    if (customMinDueAmount && Number(customMinDueAmount) > 0) count++;
    if (customMaxDueAmount && Number(customMaxDueAmount) > 0) count++;
    return count;
  }, [filters, customMinDueAmount, customMaxDueAmount]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setFilters({
      class: '',
      section: '',
      paymentStatus: '',
      dueAmountFilter: '',
      feeStructureId: ''
    });
    setCustomMinDueAmount('');
    setCustomMaxDueAmount('');
    setCurrentPage(1);
  };

  // Fast O(1) fee lookup Map by studentId to prevent O(N*M) array iterations
  const feeByStudentId = useMemo(() => {
    const map = new Map();
    if (Array.isArray(fees)) {
      for (const f of fees) {
        if (f && f.studentId) {
          map.set(String(f.studentId), f);
        }
      }
    }
    return map;
  }, [fees]);

  // Filtered Students List
  const filteredStudents = useMemo(() => {
    return students.filter((student) => {
      const fee = feeByStudentId.get(String(student.studentId));

      // Search matching name or studentId
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const matchName = student.name?.toLowerCase().includes(term);
        const matchId = student.studentId?.toLowerCase().includes(term);
        if (!matchName && !matchId) return false;
      }

      // Class & Section
      if (filters.class && student.className !== filters.class) return false;
      if (filters.section && student.section !== filters.section) return false;

      // Fee Structure
      if (filters.feeStructureId && fee?.feeStructureId !== filters.feeStructureId) return false;

      // Financials
      const total = fee?.totalFees != null
        ? Number(fee.totalFees)
        : ((fee?.items && fee.items.length > 0)
            ? fee.items.reduce((s, i) => s + (i.netAmount || i.originalAmount || 0), 0)
            : 0);
      const paid = fee?.paidFees != null ? Number(fee.paidFees) : 0;
      const concession = fee?.concessionAmount != null ? Number(fee.concessionAmount) : 0;
      const penalty = fee?.penaltyAmount != null ? Number(fee.penaltyAmount) : 0;
      const due = fee?.pendingFee != null
        ? Number(fee.pendingFee)
        : Math.max(0, total - concession + penalty - paid);

      // Payment Status
      if (filters.paymentStatus) {
        if (filters.paymentStatus === 'NOT_ASSIGNED') {
          if (fee && (fee.feeStructureId || total > 0)) return false;
        } else if (filters.paymentStatus === 'VOIDED') {
          if (fee?.status !== 'VOIDED') return false;
        } else if (filters.paymentStatus === 'PAID') {
          if (due > 0 || fee?.status !== 'PAID') return false;
        } else if (filters.paymentStatus === 'OVERDUE') {
          const isOverdue = due > 0 && fee?.installments?.some((inst) => inst.status !== 'PAID' && new Date(inst.dueDate) < new Date());
          if (!isOverdue) return false;
        } else {
          if (!fee || fee.status !== filters.paymentStatus) return false;
        }
      }

      // Due Amount Presets
      if (filters.dueAmountFilter) {
        if (filters.dueAmountFilter === 'FULLY_PAID' && due !== 0) return false;
        if (filters.dueAmountFilter === 'LESS_2000' && (due === 0 || due >= 2000)) return false;
        if (filters.dueAmountFilter === 'LESS_5000' && (due === 0 || due >= 5000)) return false;
        if (filters.dueAmountFilter === 'MORE_5000' && due <= 5000) return false;
        if (filters.dueAmountFilter === 'MORE_10000' && due <= 10000) return false;
      }

      // Custom Min / Max Due Amount
      if (customMinDueAmount && !isNaN(customMinDueAmount) && Number(customMinDueAmount) > 0) {
        if (due < Number(customMinDueAmount)) return false;
      }
      if (customMaxDueAmount && !isNaN(customMaxDueAmount) && Number(customMaxDueAmount) > 0) {
        if (due > Number(customMaxDueAmount)) return false;
      }

      return true;
    });
  }, [students, fees, feeByStudentId, searchTerm, filters, customMinDueAmount, customMaxDueAmount]);

  // Paginated Students
  const paginatedStudents = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredStudents.slice(startIndex, startIndex + pageSize);
  }, [filteredStudents, currentPage, pageSize]);

  // Selection Handlers
  const handleToggleSelect = (studentId) => {
    setSelectedStudentIds((prev) =>
      prev.includes(studentId) ? prev.filter((id) => id !== studentId) : [...prev, studentId]
    );
  };

  const handleToggleSelectAll = () => {
    const pageStudentIds = paginatedStudents.map((s) => s.studentId);
    const allSelected = pageStudentIds.every((id) => selectedStudentIds.includes(id));
    if (allSelected) {
      setSelectedStudentIds((prev) => prev.filter((id) => !pageStudentIds.includes(id)));
    } else {
      setSelectedStudentIds((prev) => Array.from(new Set([...prev, ...pageStudentIds])));
    }
  };

  // Bulk Actions
  const handleBulkAlert = () => {
    const targetStudents = students.filter((s) => {
      if (!selectedStudentIds.includes(s.studentId)) return false;
      const f = feeByStudentId.get(String(s.studentId));
      if (f?.status === 'VOIDED') return false;
      const total = f?.totalFees != null ? Number(f.totalFees) : 0;
      const paid = f?.paidFees != null ? Number(f.paidFees) : 0;
      const due = f?.pendingFee != null ? Number(f.pendingFee) : Math.max(0, total - (f?.concessionAmount || 0) + (f?.penaltyAmount || 0) - paid);
      return due > 0;
    });

    if (targetStudents.length === 0) {
      if (setDraftToast) {
        setDraftToast('ℹ️ None of the selected students have pending dues.');
        setTimeout(() => setDraftToast(null), 3000);
      }
      return;
    }

    setAlertTargetStudents(targetStudents);
    setAlertCustomTitle('Fee Payment Reminder 📢');
    setAlertCustomMessage('Dear Parent, this is an official reminder regarding your child’s pending school fee balance. Please clear the outstanding balance at your earliest convenience.');
    setShowFeeAlertModal(true);
  };

  const handleBulkExport = () => {
    const targetStudents = students.filter((s) => selectedStudentIds.includes(s.studentId));
    handleExportFeeExcel(targetStudents);
  };

  // Row Action Handlers
  const handleAlertParent = (student, due) => {
    setAlertTargetStudents([student]);
    setAlertCustomTitle('Fee Reminder Alert 📢');
    setAlertCustomMessage(`Dear Parent of ${student.name}, your child's remaining fee balance is ₹${Number(due).toLocaleString('en-IN')}. Please clear it at your earliest convenience.`);
    setShowFeeAlertModal(true);
  };

  const handleCollectPayment = async (student, fee) => {
    setSelectedStudentForCollection(student);
    setCollectionFeeRecord(fee);
    setFeesSubTab('collection');
    try {
      const txs = await feeService.getTransactionHistory(student.studentId, orgData?.orgId);
      setCollectionPayments(txs || []);
    } catch (err) {
      console.error('Failed to fetch collection transactions:', err);
    }
  };

  const handleViewHistory = async (student) => {
    setSelectedStudentForHistory(student);
    setShowHistoryModal(true);
    setLoadingPaymentHistory(true);
    try {
      const txs = await feeService.getTransactionHistory(student.studentId, orgData?.orgId);
      setHistoryPayments(txs || []);
    } catch (err) {
      console.error('Failed to load transaction history:', err);
    } finally {
      setLoadingPaymentHistory(false);
    }
  };

  return (
    <div>
      {/* 1. Header Toolbar */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '8px',
          marginBottom: '12px'
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: '19px', fontWeight: '800', color: '#0f172a' }}>
            Fee Management
          </h2>
          <p style={{ margin: '2px 0 0 0', color: '#64748b', fontSize: '12px' }}>
            Review student fee structures, track collections, and manage outstanding balances.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              setSelectedStructure(null);
              setStructureFormData({
                name: '',
                academicYear: '2026-27',
                classId: '',
                className: '',
                totalAmount: 0,
                items: [{ feeType: 'Tuition Fee', amount: '' }],
                installments: [{ installmentName: 'Term 1', amount: '', dueDate: '' }]
              });
              setShowCreateStructureModal(true);
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              height: '30px',
              fontSize: '11.5px',
              padding: '0 10px',
              fontWeight: '600',
              borderRadius: '6px'
            }}
          >
            <Plus size={14} /> Structure
          </button>

          <button
            type="button"
            className="btn btn-success"
            onClick={() => {
              setAssignPreview(null);
              setAssignFormData({
                feeStructureId: '',
                className: '',
                section: '',
                overwrite: false
              });
              setShowAssignStructureModal(true);
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              height: '30px',
              fontSize: '11.5px',
              padding: '0 10px',
              fontWeight: '600',
              borderRadius: '6px'
            }}
          >
            <Users size={14} /> Bulk Assign
          </button>

          <button
            type="button"
            className="btn btn-outline-success"
            onClick={() => handleExportFeeExcel(filteredStudents)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              height: '30px',
              fontSize: '11.5px',
              padding: '0 10px',
              fontWeight: '600',
              borderRadius: '6px'
            }}
            title="Download Filtered Dues Report as Excel Spreadsheet"
          >
            <FileSpreadsheet size={14} /> Excel
          </button>

          <button
            type="button"
            className="btn btn-outline-danger"
            onClick={() => handleExportFeePDF(filteredStudents)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              height: '30px',
              fontSize: '11.5px',
              padding: '0 10px',
              fontWeight: '600',
              borderRadius: '6px'
            }}
            title="Download Filtered Dues Report as PDF Document"
          >
            <FileText size={14} /> PDF
          </button>

          <button
            type="button"
            className="btn btn-light"
            onClick={loadFees}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              height: '30px',
              fontSize: '11.5px',
              padding: '0 10px',
              border: '1px solid #cbd5e1',
              fontWeight: '600',
              borderRadius: '6px'
            }}
            title="Reload Fee Records from Server"
          >
            <RefreshCw size={13} /> Refresh
          </button>
        </div>
      </div>

      {/* 2. Compact KPI Summary Cards */}
      <FeeSummaryCards students={students} fees={fees} loading={loading} />

      {/* 3. Filter Bar */}
      <FeeFiltersBar
        searchTerm={searchTerm}
        onSearchChange={(val) => {
          setSearchTerm(val);
          setCurrentPage(1);
        }}
        filters={filters}
        onFilterChange={handleFilterChange}
        onResetFilters={handleResetFilters}
        classes={availableClasses}
        onOpenAdvancedFilters={() => setShowAdvancedFilters(true)}
        activeFilterCount={activeFilterCount}
        selectedStudentIds={selectedStudentIds}
        onClearSelection={() => setSelectedStudentIds([])}
        onBulkAlert={handleBulkAlert}
        onBulkExport={handleBulkExport}
      />

      {/* 4. High-Density Spreadsheet Table */}
      <FeeTable
        students={paginatedStudents}
        fees={fees}
        feeByStudentId={feeByStudentId}
        loading={loading}
        selectedStudentIds={selectedStudentIds}
        onToggleSelect={handleToggleSelect}
        onToggleSelectAll={handleToggleSelectAll}
        onAlertParent={handleAlertParent}
        onCollectPayment={handleCollectPayment}
        onEditFee={handleOpenEditFeeModal}
        onViewHistory={handleViewHistory}
        onViewStudentDrawer={(st, f) => setStudentForDrawer({ student: st, fee: f })}
        onGenerateClearanceCert={handleOpenClearanceModal}
      />

      {/* 5. Pagination */}
      <FeePagination
        currentPage={currentPage}
        pageSize={pageSize}
        totalItems={filteredStudents.length}
        onPageChange={(p) => setCurrentPage(p)}
        onPageSizeChange={(sz) => {
          setPageSize(sz);
          setCurrentPage(1);
        }}
      />

      {/* 6. Quick View Student Drawer */}
      <FeeStudentDrawer
        isOpen={Boolean(studentForDrawer)}
        onClose={() => setStudentForDrawer(null)}
        student={studentForDrawer?.student}
        fee={studentForDrawer?.fee}
        feeService={feeService}
        orgId={orgData?.orgId}
        onEditFee={handleOpenEditFeeModal}
        onCollectPayment={handleCollectPayment}
        onAlertParent={handleAlertParent}
        onViewHistory={handleViewHistory}
        onGenerateClearanceCert={handleOpenClearanceModal}
      />

      {/* 7. History Drawer */}
      <FeeHistoryDrawer
        isOpen={Boolean(studentForHistoryDrawer)}
        onClose={() => setStudentForHistoryDrawer(null)}
        student={studentForHistoryDrawer}
        feeService={feeService}
        orgId={orgData?.orgId}
      />

      {/* 8. Advanced Filters Drawer */}
      <FeeAdvancedFiltersDrawer
        isOpen={showAdvancedFilters}
        onClose={() => setShowAdvancedFilters(false)}
        classes={availableClasses}
        feeStructures={feeStructures}
        filters={filters}
        onFilterChange={handleFilterChange}
        onResetFilters={handleResetFilters}
        customMinDueAmount={customMinDueAmount}
        setCustomMinDueAmount={(val) => {
          setCustomMinDueAmount(val);
          setCurrentPage(1);
        }}
        customMaxDueAmount={customMaxDueAmount}
        setCustomMaxDueAmount={(val) => {
          setCustomMaxDueAmount(val);
          setCurrentPage(1);
        }}
        totalFilteredCount={filteredStudents.length}
      />

      {/* 9. Fee Clearance / No Dues Certificate Modal */}
      {clearanceModalData.isOpen && (
        <FeeClearanceCertificateModal
          isOpen={clearanceModalData.isOpen}
          onClose={() => setClearanceModalData({ isOpen: false, student: null, fee: null })}
          student={clearanceModalData.student}
          fee={clearanceModalData.fee}
          academicYear={clearanceModalData.fee?.academicYear || orgData?.academicYear || '2026-2027'}
          orgData={orgData}
          onCertificateIssued={(cert) => {
            if (showNotification) {
              showNotification('success', 'Certificate Issued', `Official Fee Clearance Certificate #${cert.certificateNumber} issued successfully.`);
            }
          }}
        />
      )}
    </div>
  );
};

export default FeeManagementWorkspace;
